import { getServerConfig } from '@/utils/config';
import { HttpMethods, tryFetchToJson } from '@/utils/http-request';

import { SimpleLocationDto } from '../locations/dto/simple-location.dto';
import { DayPassFilterDto } from './dto/coworking-day-pass-filter.dto';
import { CoworkingDayPassPricePreviewDto } from './dto/coworking-day-pass-price-preview.dto';
import { CoworkingDayPassPricesDto } from './dto/coworking-day-pass-price.dto';
import { OpenWorkspaceReservationDto } from './dto/open-workspace-reservation.dto';

export async function searchCoworkingDayPassPricePreview(params: {
  locationId: string;
  nbDays: number;
  promoCode?: string;
}): Promise<CoworkingDayPassPricePreviewDto> {
  const { locationId, nbDays, promoCode } = params;

  const host = getServerConfig().NEXT_PUBLIC_PRICING_API_URL;

  const url = `${host}/discovery/locations/${locationId}/coworking-day-pass/preview`;
  const body = {
    nbDays,
    promoCode,
  };

  return tryFetchToJson<CoworkingDayPassPricePreviewDto>({
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(body),
      },
    },
    skipFetchError: true,
  });
}

export async function createOpenWorkspaceReservation(params: {
  locationId: string;
  startDay: string;
  endDay: string;
  tmpAccess?: {
    reserverId: string;
    tempAccessKey: string;
  };
  callbackError: () => void;
}): Promise<OpenWorkspaceReservationDto> {
  const { locationId, startDay, endDay, tmpAccess, callbackError } = params;
  const host = getServerConfig().NEXT_PUBLIC_RESERVATION_API_URL;

  const url = params.tmpAccess
    ? `${host}/open-workspace-reservations-temp-access`
    : `${host}/open-workspace-reservations`;

  const body = {
    locationId,
    startDay,
    endDay,
    ...(tmpAccess && { ...tmpAccess }),
  };

  return tryFetchToJson<OpenWorkspaceReservationDto>({
    withAuthToken: tmpAccess == null,
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Content-Type': 'application/json',
        },
        method: HttpMethods.Post,
        body: JSON.stringify(body),
      },
    },
    onFunctionalError: callbackError,
  });
}

export async function updateOpenWorkspaceReservation(params: {
  reservationId: string;
  startDay: string;
  endDay: string;
  tmpAccess?: {
    reserverId: string;
    tempAccessKey: string;
  };
  paymentIntentId?: string;
  callbackError: () => void;
}): Promise<OpenWorkspaceReservationDto> {
  const {
    reservationId,
    startDay,
    endDay,
    tmpAccess,
    paymentIntentId,
    callbackError,
  } = params;
  const host = getServerConfig().NEXT_PUBLIC_RESERVATION_API_URL;

  const url = params.tmpAccess
    ? `${host}/open-workspace-reservations-temp-access/${reservationId}`
    : `${host}/open-workspace-reservations/${reservationId}`;

  const body = {
    startDay,
    endDay,
    ...(tmpAccess && { ...tmpAccess }),
    ...(paymentIntentId && { paymentIntentId }),
  };

  return tryFetchToJson<OpenWorkspaceReservationDto>({
    withAuthToken: tmpAccess == null,
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Content-Type': 'application/json',
        },
        method: HttpMethods.Put,
        body: JSON.stringify(body),
      },
    },
    onFunctionalError: callbackError,
  });
}

export async function fetchOpenWorkspaceReservationById(params: {
  id: string;
  userId?: string;
  tempAccessKey?: string;
  options?: {
    onSignUp?: boolean;
  };
}) {
  const { id, userId, tempAccessKey, options = {} } = params;

  const host = getServerConfig().NEXT_PUBLIC_RESERVATION_API_URL;

  const url = options.onSignUp
    ? `${host}/open-workspace-reservations-temp-access/${id}?userId=${userId}&tempAccessKey=${tempAccessKey}`
    : `${host}/open-workspace-reservations/${id}`;

  return tryFetchToJson<OpenWorkspaceReservationDto>({
    withAuthToken: !options?.onSignUp,
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    },
  });
}

export async function confirmReservation(params: {
  reservationId: string;
  userId?: string;
  tempAccessKey?: string;
}): Promise<OpenWorkspaceReservationDto> {
  const { reservationId, userId, tempAccessKey } = params;

  const host = getServerConfig().NEXT_PUBLIC_RESERVATION_API_URL;

  const url =
    userId != null
      ? `${host}/open-workspace-reservations-temp-access/${reservationId}/confirm`
      : `${host}/open-workspace-reservations/${reservationId}/confirm`;

  return tryFetchToJson<OpenWorkspaceReservationDto>({
    withAuthToken: tempAccessKey == null,
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(userId != null ? { userId } : {}),
          ...(tempAccessKey != null ? { tempAccessKey } : {}),
        }),
        method: HttpMethods.Post,
      },
    },
  });
}
export async function fetchDayPassLocationPricingByIds(
  locationIds: string[]
): Promise<CoworkingDayPassPricesDto> {
  const { NEXT_PUBLIC_PRICING_API_URL } = getServerConfig();
  const url = `${NEXT_PUBLIC_PRICING_API_URL}/discovery/coworking`;

  const locationsPricesDto = await tryFetchToJson<CoworkingDayPassPricesDto>({
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Content-Type': 'application/json',
        },
        method: HttpMethods.Post,
        body: JSON.stringify({ locationIds }),
      },
    },
  });

  return locationsPricesDto;
}

export type SearchDayPassesLocationsProps = {
  locale: string;
} & DayPassFilterDto;

export async function searchDayPassesLocations({
  locale,
  ...filters
}: SearchDayPassesLocationsProps) {
  const host = getServerConfig().NEXT_PUBLIC_INVENTORY_API_URL;

  const url = `${host}/discovery/day-passes/search`;

  const locations = await tryFetchToJson<SimpleLocationDto[]>({
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Accept-Language': locale,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(filters),
      },
    },
    skipFetchError: true,
  });

  return locations ?? [];
}

export async function cancelOpenWorkspaceReservation(id: string) {
  const url = `${
    getServerConfig().NEXT_PUBLIC_RESERVATION_API_URL
  }/open-workspace-reservations/${id}`;

  return tryFetchToJson<OpenWorkspaceReservationDto>({
    withAuthToken: true,
    fetchParams: {
      input: url,
      init: {
        method: HttpMethods.Delete,
      },
    },
  });
}

export async function getCoworkingDayPassUnavailableDays(
  locationIdOrSlug: string
): Promise<string[]> {
  const host = getServerConfig().NEXT_PUBLIC_RESERVATION_API_URL;

  const url = `${host}/locations/${locationIdOrSlug}/coworking-day-pass/unavailable-days`;

  const data = await tryFetchToJson<{
    unavailableDays: string[];
  }>({
    fetchParams: {
      input: url,
      init: {
        method: HttpMethods.Get,
      },
    },
  });

  return data ? data.unavailableDays : [];
}

export async function applyPromoCode(params: {
  reservationId: string;
  promoCode: string;
  paymentIntentId: string;
  tempAccessKey?: string;
  reserverId?: string;
}): Promise<OpenWorkspaceReservationDto | undefined> {
  const {
    reservationId,
    promoCode,
    paymentIntentId,
    tempAccessKey,
    reserverId,
  } = params;

  const host = getServerConfig().NEXT_PUBLIC_RESERVATION_API_URL;

  const url = tempAccessKey
    ? `${host}/open-workspace-reservations-temp-access/${reservationId}/promo-code`
    : `${host}/open-workspace-reservations/${reservationId}/promo-code`;

  const body = {
    promoCode,
    paymentIntentId,
    ...(tempAccessKey && { tempAccessKey }),
    ...(reserverId && { reserverId }),
  };

  return tryFetchToJson<OpenWorkspaceReservationDto | undefined>({
    withAuthToken: !tempAccessKey,
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        method: HttpMethods.Post,
      },
    },
  });
}

export async function unapplyPromoCode(params: {
  reservationId: string;
  promoCode: string;
  paymentIntentId: string;
  tempAccessKey?: string;
  reserverId?: string;
}): Promise<OpenWorkspaceReservationDto | undefined> {
  const {
    reservationId,
    promoCode,
    paymentIntentId,
    tempAccessKey,
    reserverId,
  } = params;

  const host = getServerConfig().NEXT_PUBLIC_RESERVATION_API_URL;

  const url = tempAccessKey
    ? `${host}/open-workspace-reservations-temp-access/${reservationId}/promo-code`
    : `${host}/open-workspace-reservations/${reservationId}/promo-code`;

  const body = {
    promoCode,
    paymentIntentId,
    ...(tempAccessKey && { tempAccessKey }),
    ...(reserverId && { reserverId }),
  };

  return tryFetchToJson<OpenWorkspaceReservationDto | undefined>({
    withAuthToken: !tempAccessKey,
    fetchParams: {
      input: url,
      init: {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        method: HttpMethods.Delete,
      },
    },
  });
}
